import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Star, MapPin, SlidersHorizontal, X } from 'lucide-react';
import { api } from '../context/AuthContext';
import { grossFromNet, fmtMoney } from '../lib/fees';

// Skills may have been typed as one long comma-separated entry; show them as separate chips.
const splitSkills = (skills) => (Array.isArray(skills) ? skills : [skills || ''])
  .flatMap(s => String(s).split(/[,;\n]+/)).map(s => s.trim()).filter(Boolean);
import './Discovery.css';

// Sort options
const SORT_OPTS = [
  { value: 'top_rated', label: 'Top rated' },
  { value: 'newest',    label: 'Newest' },
  { value: 'price_asc', label: 'Price: Low → High' },
  { value: 'price_desc',label: 'Price: High → Low' },
];

const priceOf = (u) => {
  const pkgs = u?.booking_packages || [];
  if (!pkgs.length) return null;
  return Math.min(...pkgs.map(p => grossFromNet(p.price)).filter(n => !isNaN(n)));
};

const Discovery = () => {
  const [profiles, setProfiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [sortBy, setSortBy] = useState('top_rated');
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await api('/api/users?limit=100');
        const clean = (data.users || []).filter((u) => {
          const n = (u.full_name || '').trim();
          return !/^(TEST|Test Video|QA )/i.test(n);
        });
        setProfiles(clean);
      } catch {
        setProfiles([]);
      }
      setLoading(false);
    };
    fetchUsers();
  }, []);

  // Aggregate all unique skills across profiles for the chip filter row
  const allSkills = useMemo(() => {
    const set = new Set();
    profiles.forEach(p => splitSkills(p.skills).forEach(s => set.add(s)));
    return Array.from(set).sort();
  }, [profiles]);

  const toggleSkill = (skill) => {
    setSelectedSkills(prev =>
      prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]
    );
  };

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let out = profiles.filter(p => {
      const nameHit = !q || p.full_name?.toLowerCase().includes(q) || p.username?.toLowerCase().includes(q);
      const skillMatchQuery = !q || splitSkills(p.skills).some(s => s.toLowerCase().includes(q));
      const passesSearch = nameHit || skillMatchQuery;
      const passesChips = selectedSkills.length === 0 ||
        selectedSkills.every(sk => splitSkills(p.skills).includes(sk));
      return passesSearch && passesChips;
    });

    switch (sortBy) {
      case 'top_rated':
        out.sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0));
        break;
      case 'newest':
        out.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
      case 'price_asc':
        out.sort((a, b) => {
          const pa = priceOf(a); const pb = priceOf(b);
          if (pa === null && pb === null) return 0;
          if (pa === null) return 1;
          if (pb === null) return -1;
          return pa - pb;
        });
        break;
      case 'price_desc':
        out.sort((a, b) => (priceOf(b) ?? -Infinity) - (priceOf(a) ?? -Infinity));
        break;
      default: break;
    }
    return out;
  }, [profiles, searchQuery, selectedSkills, sortBy]);

  const clearAll = () => { setSearchQuery(''); setSelectedSkills([]); setSortBy('top_rated'); };

  return (
    <div className="discovery-container" data-testid="discovery-page">
      <div className="discovery-header">
        <div className="discovery-title-row">
          <div>
            <h1 className="discovery-h1">Discover Mates</h1>
            <p className="discovery-sub">Find a mentor whose lifetime of wisdom matches your goals</p>
          </div>
          <button
            className={`discovery-filter-toggle ${filtersOpen ? 'active' : ''}`}
            onClick={() => setFiltersOpen(v => !v)}
            data-testid="discovery-filters-toggle"
            aria-label="Toggle filters"
          >
            <SlidersHorizontal size={18} />
          </button>
        </div>

        <div className="discovery-search-row">
          <div className="discovery-search-bar">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search by name or skill..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="discovery-search"
            />
            {searchQuery && (
              <button className="clear-btn" onClick={() => setSearchQuery('')} aria-label="Clear search">
                <X size={14} />
              </button>
            )}
          </div>
          <select
            className="discovery-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            data-testid="discovery-sort"
          >
            {SORT_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {(filtersOpen || selectedSkills.length > 0) && allSkills.length > 0 && (
          <div className="discovery-chips-row" data-testid="discovery-skill-chips">
            {allSkills.slice(0, 40).map(sk => {
              const isOn = selectedSkills.includes(sk);
              return (
                <button
                  key={sk}
                  className={`discovery-chip ${isOn ? 'on' : ''}`}
                  onClick={() => toggleSkill(sk)}
                  data-testid={`skill-chip-${sk.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {sk}
                </button>
              );
            })}
            {(selectedSkills.length > 0 || searchQuery) && (
              <button className="discovery-chip clear" onClick={clearAll} data-testid="discovery-clear-filters">
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="discovery-empty" data-testid="discovery-loading">Loading mates…</div>
      ) : filtered.length === 0 ? (
        <div className="discovery-empty" data-testid="discovery-empty">
          <p className="empty-h">No mates match those filters</p>
          <p className="empty-sub">Try clearing filters or broadening your search.</p>
          {(selectedSkills.length > 0 || searchQuery) && (
            <button className="discovery-empty-btn" onClick={clearAll}>Clear filters</button>
          )}
        </div>
      ) : (
        <div className="discovery-grid" data-testid="discovery-grid">
          {filtered.map((p) => {
            const price = priceOf(p);
            const avatar = p.avatar_url || `https://i.pravatar.cc/150?u=${p.id}`;
            return (
              <article
                key={p.id}
                className="discovery-card"
                onClick={() => navigate(`/discovery/${p.id}`)}
                data-testid={`profile-card-${p.id}`}
              >
                <div className="discovery-card-top">
                  <img src={avatar} alt={p.full_name} className="discovery-avatar" />
                  {p.average_rating > 0 && (
                    <div className="discovery-rating-badge" data-testid={`rating-${p.id}`}>
                      <Star size={12} fill="#D97706" color="#D97706" />
                      <span>{Number(p.average_rating).toFixed(1)}</span>
                    </div>
                  )}
                </div>
                <div className="discovery-card-body">
                  <h3 className="discovery-name">{p.full_name}</h3>
                  {p.location && (
                    <p className="discovery-location">
                      <MapPin size={12} /> {p.location}
                    </p>
                  )}
                  {p.bio && <p className="discovery-bio" data-testid={`profile-bio-${p.id}`}>{p.bio}</p>}
                  {(() => {
                    const skills = splitSkills(p.skills);
                    if (!skills.length) return null;
                    return (
                      <>
                        <div className="discovery-skills" data-testid={`profile-skills-${p.id}`}>
                          {skills.slice(0, 3).map((s, i) => (
                            <span key={i} className="discovery-skill" title={s}>{s}</span>
                          ))}
                        </div>
                        {skills.length > 3 && <span className="discovery-skill-more" data-testid={`profile-skills-more-${p.id}`}>+{skills.length - 3} more</span>}
                      </>
                    );
                  })()}
                </div>
                <div className="discovery-card-footer">
                  <div className="discovery-price">
                    {price !== null ? (
                      <>
                        <span className="price-label">From</span>
                        <span className="price-value" data-testid={`profile-price-${p.id}`}>{fmtMoney(price)}</span>
                      </>
                    ) : (
                      <span className="price-tbd">Chat to book</span>
                    )}
                  </div>
                  <button
                    className="discovery-book-btn"
                    onClick={(e) => { e.stopPropagation(); navigate(`/discovery/${p.id}`); }}
                    data-testid={`book-btn-${p.id}`}
                  >
                    Book
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Discovery;
